import { Box, Theme } from '@mui/material';
import { ImgHTMLAttributes, MouseEventHandler, useEffect, useRef, useState } from 'react';
import { makeStyles } from 'tss-react/mui';
import { useShallow } from 'zustand/react/shallow';
import useWidth from '../../../../components/Hooks/useWidth';
import useMediaStyles from '../../../../components/useMediaStyles';
import useGeneralSettingsStore from '../../../../stores/GeneralSettings/useGeneralSettingsStore';
import { MediaType } from '../../../../types/general.types';
import { getMediaAspectRatio } from '../../../../utils/getMediaAspectRatio';
import usePinchZoom from './hooks/usePinchZoom';
import { getNormalizedSafeUrl } from './utils/getNormalizedSafeUrl';

const useStyles = makeStyles()((theme: Theme) => ({
  mainImage: {
    [theme.breakpoints.up('sm')]: {
      opacity: 1,
    },
  },
  mainImageAbsolute: {
    [theme.breakpoints.up('sm')]: {
      position: 'absolute',
      left: 0,
    },
  },
  previewImage: {
    [theme.breakpoints.up('sm')]: {
      position: 'absolute',
      top: 0,
      left: 0,
      opacity: 1,
    },
  },
  previewImageLoaded: {
    display: 'none',
  },
  rounded: {
    borderRadius: theme.shape.borderRadius,
  },
  centeredImageContainer: {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    height: '100%',
  },
}));

const ZOOM_CONTAINER_SX = { height: '100%', overflowX: 'auto', position: 'relative' } as const;
const GALLERY_CONTAINER_SX = { height: '100%' } as const;

type ProductMediaRendererProps = {
  mediaUrl: string;
  previewUrl: string;
  type: Omit<MediaType, MediaType.PDF>;
  isZoom?: boolean;
  isZoomed?: boolean;
  imageProps?: ImgHTMLAttributes<HTMLImageElement>;
  onMobileContainerCover?: boolean;
  focusPercentToScroll?: number;
  onClick?: MouseEventHandler<HTMLImageElement>;
};

const ProductMediaRenderer = ({
  previewUrl,
  mediaUrl,
  type,
  imageProps,
  isZoom,
  isZoomed,
  onClick,
  focusPercentToScroll,
  onMobileContainerCover,
}: ProductMediaRendererProps) => {
  const { classes, cx } = useStyles();
  const previewImageRef = useRef<HTMLImageElement>(null);
  const mainImageRef = useRef<HTMLImageElement>(null);
  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const [imagePhase, setImagePhase] = useState<'preview' | 'loaded'>('preview');
  const { mobileWidth, largeWidthUp } = useWidth();

  const { mediaFormat } = useGeneralSettingsStore(
    useShallow((state) => ({
      mediaFormat: state.generalSettings.mediaFormat,
    })),
  );

  const { classes: mediaClasses } = useMediaStyles({
    imageAspectRatioPadding: getMediaAspectRatio(mediaFormat),
  });

  const mediaBoxClass =
    isZoom || (onMobileContainerCover && mobileWidth) || largeWidthUp
      ? mediaClasses.mediaBoxWithoutAspectRatio
      : mediaClasses.mediaBoxWithAspectRatio;

  const shouldCenterMedia = mobileWidth || (isZoom && !isZoomed);

  const syncScroll = () => {
    const container = scrollContainerRef.current;
    const image = mainImageRef.current;

    if (!container || !image || focusPercentToScroll === undefined) {
      return;
    }

    const maxScroll = Math.max(0, image.offsetHeight - container.clientHeight);
    const clampedPercent = Math.min(Math.max(focusPercentToScroll, 0), 1);

    container.scrollTo({ top: clampedPercent * maxScroll, behavior: 'instant' });
  };

  usePinchZoom({
    imageRef: mainImageRef,
    imgContainerRef: scrollContainerRef,
    enabled: mobileWidth,
  });

  // biome-ignore lint/correctness/useExhaustiveDependencies: <reposiciona a rolagem quando o ponto de foco ou o zoom mudam>
  useEffect(syncScroll, [focusPercentToScroll, isZoomed]);

  const normalizedMediaUrl = getNormalizedSafeUrl(mediaUrl);
  const normalizedPreviewUrl = getNormalizedSafeUrl(previewUrl);

  return (
    <Box
      ref={scrollContainerRef}
      sx={{
        maxWidth: '100%',
        overflowY: 'auto',
        ...(isZoom ? ZOOM_CONTAINER_SX : GALLERY_CONTAINER_SX),
      }}
    >
      {type === 'IMAGE' ? (
        <>
          {!mobileWidth && (
            <Box
              className={cx({
                [mediaBoxClass]: true,
                [classes.rounded]: true,
                [classes.previewImage]: true,
                [classes.centeredImageContainer]: shouldCenterMedia,
                [classes.previewImageLoaded]: imagePhase === 'loaded',
              })}
            >
              <img
                ref={previewImageRef}
                src={normalizedPreviewUrl}
                alt="teceo"
                onClick={onClick}
                className={mediaClasses.mediaContentCover}
                {...imageProps}
              />
            </Box>
          )}
          <Box
            className={cx({
              [mediaBoxClass]: true,
              [classes.mainImage]: true,
              [classes.mainImageAbsolute]: !isZoom,
              [classes.centeredImageContainer]: shouldCenterMedia,
            })}
          >
            <img
              ref={mainImageRef}
              src={normalizedMediaUrl}
              alt="teceo"
              loading="lazy"
              onClick={onClick}
              className={mediaClasses.mediaContentCover}
              onLoad={() => {
                syncScroll();
                setImagePhase('loaded');
              }}
              {...imageProps}
              onError={(event) => {
                const image = event.currentTarget;
                const normalizedUrl = getNormalizedSafeUrl(image.src);

                if (image.src !== normalizedUrl) {
                  image.src = normalizedUrl;
                }
              }}
            />
          </Box>
        </>
      ) : (
        <Box
          className={cx({
            [mediaBoxClass]: true,
            [classes.mainImage]: true,
            [classes.mainImageAbsolute]: !isZoom,
          })}
        >
          <video
            src={mediaUrl}
            className={mediaClasses.mediaContentCover}
            loop
            autoPlay
            muted
            disablePictureInPicture
            playsInline
          />
        </Box>
      )}
    </Box>
  );
};

export default ProductMediaRenderer;
